import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Divider,
  CircularProgress,
  makeStyles,
  useTheme
} from "@material-ui/core";
import { APP_VERSION } from '../../config/version';
import api from '../../services/api';
import { useTimezone } from '../../hooks/useTimezone';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  sectionCard: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.mode === 'dark' 
        ? '0 4px 16px rgba(0,0,0,0.5)' 
        : '0 4px 16px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    },
  },
  sectionHeader: {
    background: theme.mode === 'dark' 
      ? 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)' 
      : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: 'white',
    padding: theme.spacing(2),
    borderRadius: '12px 12px 0 0',
    marginBottom: 0,
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionContent: {
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.background.paper,
  },
  settingItem: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
    transition: 'all 0.2s ease',
    height: '100%',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e3f2fd',
      borderColor: theme.mode === 'dark' ? theme.palette.primary.light : '#1976d2',
    },
  },
  settingLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  versionBadge: {
    fontSize: '16px',
    fontWeight: 700,
    padding: '6px 16px',
  },
  infoBox: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.mode === 'dark' ? '#1e3a8a' : '#e3f2fd',
    border: `1px solid ${theme.mode === 'dark' ? '#3b82f6' : '#1976d2'}`,
    marginTop: theme.spacing(2),
  },
  highlightList: {
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    "& li": {
      marginBottom: theme.spacing(0.75),
      lineHeight: 1.45,
    },
  },
}));

function formatReleaseLabel(dateStr, formatDate, formatDateTime, naLabel = "N/A") {
  if (!dateStr || dateStr === "N/A") return naLabel;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const naive = `${dateStr} 00:00:00`;
    if (formatDate) {
      const d = formatDate(naive);
      if (d) return d;
    }
    try {
      return new Date(`${dateStr}T12:00:00`).toLocaleDateString();
    } catch {
      return dateStr;
    }
  }
  try {
    return formatDateTime ? formatDateTime(dateStr) : String(dateStr);
  } catch {
    return String(dateStr);
  }
}

export default function VersionInfo() {
  const classes = useStyles();
  const theme = useTheme();
  const { formatDateTime, formatDate } = useTimezone();
  const [pkgVersion, setPkgVersion] = useState(null);
  const [semantic, setSemantic] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(true);

  useEffect(() => {
    const loadVersionInfo = async () => {
      setLoadingVersion(true);
      try {
        const [sysRes, pkgRes] = await Promise.all([
          api.get("/system/version-info").catch(() => ({ data: null })),
          api.get("/version/version").catch(() => ({ data: null })),
        ]);
        if (sysRes?.data?.semanticVersion) {
          setSemantic(sysRes.data.semanticVersion);
        }
        if (pkgRes?.data) {
          setPkgVersion(pkgRes.data);
        }
      } catch (error) {
        console.error("Error cargando información de versión:", error);
      } finally {
        setLoadingVersion(false);
      }
    };
    loadVersionInfo();
  }, []);

  const displayVersion =
    semantic?.currentVersion || pkgVersion?.version || APP_VERSION;
  const lastUpdateLabel = formatReleaseLabel(
    semantic?.lastVersionUpdate || semantic?.lastReleaseDate,
    formatDate,
    formatDateTime,
    i18n.t("settings.versionPage.na")
  );
  const latest = semantic?.latestRelease;

  if (loadingVersion) {
    return (
      <Box className={classes.container}>
        <Card className={classes.sectionCard}>
          <CardContent className={classes.sectionContent}>
            <Box className={classes.loadingContainer}>
              <CircularProgress size={40} />
              <Typography variant="body2" style={{ color: '#6B7280' }}>
                {i18n.t("settings.versionPage.loading")}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* ✅ INFORMACIÓN PRINCIPAL DE VERSIÓN */}
      <Card className={classes.sectionCard}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span role="img" aria-label={i18n.t("settings.versionPage.ariaVersion")}>📦</span> {i18n.t("settings.versionPage.title")}
          </Typography>
        </Box>
        <CardContent className={classes.sectionContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.versionPage.currentVersion")}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                  <Chip
                    label={`v${displayVersion}`}
                    className={classes.versionBadge}
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                    }}
                  />
                  {latest?.version && latest.version !== displayVersion && (
                    <Chip
                      label={i18n.t("settings.versionPage.changelogChip", { version: latest.version })}
                      size="small"
                      variant="outlined"
                      style={{ fontSize: '11px' }}
                    />
                  )}
                </Box>
                {latest?.date && (
                  <Typography variant="caption" style={{ display: 'block', marginTop: 8, color: '#6B7280' }}>
                    {i18n.t("settings.versionPage.releaseDateChangelog")}{" "}
                    {formatReleaseLabel(latest.date, formatDate, formatDateTime, i18n.t("settings.versionPage.na"))}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.versionPage.changeType")}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                  <Chip
                    label="MINOR"
                    size="small"
                    style={{
                      backgroundColor: "#3b82f6",
                      color: "white",
                      fontSize: "11px",
                      fontWeight: 600,
                    }}
                  />
                  {semantic?.lastUpdateSource === "git-last-commit" && (
                    <Chip
                      label={i18n.t("settings.versionPage.gitDateChip")}
                      size="small"
                      style={{ fontSize: "10px" }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>

          {latest?.highlights?.length > 0 && (
            <Box className={classes.infoBox}>
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 600, marginBottom: 8, color: theme.palette.text.primary }}
              >
                {i18n.t("settings.versionPage.releaseSummary")}
                {latest.version ? ` (v${latest.version})` : ""}
                {typeof latest.totalChanges === "number" && latest.totalChanges > 0
                  ? ` · ${i18n.t("settings.versionPage.changesCount", { count: latest.totalChanges })}`
                  : ""}
              </Typography>
              <ul className={classes.highlightList}>
                {latest.highlights.slice(0, 12).map((line, idx) => (
                  <li key={idx}>
                    <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
                      {line}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}

          <Divider style={{ margin: '20px 0' }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.versionPage.frontendVersion")}
                </Typography>
                <Typography variant="body2" style={{ marginTop: 8, color: '#6B7280', fontWeight: 500 }}>
                  v{APP_VERSION}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.versionPage.apiVersion")}
                </Typography>
                <Typography variant="body2" style={{ marginTop: 8, color: '#6B7280', fontWeight: 500 }}>
                  {pkgVersion?.version ? `v${pkgVersion.version}` : i18n.t("settings.versionPage.na")}
                  {pkgVersion?.versionSystem ? (
                    <Typography component="span" variant="caption" style={{ display: 'block', color: '#9CA3AF' }}>
                      {i18n.t("settings.versionPage.baseWhaticket")} {pkgVersion.versionSystem}
                    </Typography>
                  ) : null}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.versionPage.lastUpdate")}
                </Typography>
                <Typography variant="body2" style={{ marginTop: 8, color: '#6B7280', fontWeight: 500 }}>
                  {lastUpdateLabel}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider style={{ margin: '20px 0' }} />
          
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="caption" style={{ color: '#6B7280' }}>
              {i18n.t("settings.versionPage.semanticFooter")}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.location.href = '/releases'}
              style={{
                textTransform: 'none',
                borderRadius: 8,
                fontSize: '12px',
                padding: '6px 16px'
              }}
            >
              {i18n.t("settings.versionPage.fullHistory")}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

